import { useEffect } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { VideoPlayer } from '@/components/VideoPlayer';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useSourcesStore } from '@/stores';
import { StreamingMode } from '@/types/streaming';
import { EngineService } from '@/services/engine';
import { getSourceCacheKey } from '@/constants/cache-keys';
import { stripFileExtension } from '@/utils/file-name';
import { getEventStartTime } from '@/utils/event-duration';
import { buildBoundingBoxProps } from '@/utils/bounding-box';
import { deriveEventPlaybackRange } from '@/utils/event-playback-range';
import { usePlayerStateReporter } from '@/hooks';
import type { DirectoryItemOccurrence } from '../types';
import { EventDescription } from './EventDescription';
import { EventsTabSkeleton } from './EventsTabSkeleton';

interface DirectoryItemEventsTabProps {
    occurrence: DirectoryItemOccurrence | null;
    isOccurrencesLoading: boolean;
    hasOccurrences: boolean;
}

export function DirectoryItemEventsTab({
    occurrence,
    isOccurrencesLoading,
    hasOccurrences,
}: DirectoryItemEventsTabProps) {
    const { t } = useTranslation(I18nNamespace.DIRECTORY);
    const { getSourceById, addSource, updateSource } = useSourcesStore();
    const { onTimeUpdate, onPlayingChange, onLoopChange, onSeeked } =
        usePlayerStateReporter({
            isLive: false,
            playingEventId: occurrence?.event.id ?? null,
        });

    const currentEvent = occurrence?.event;
    const confidence = occurrence?.confidence;
    const sourceId = currentEvent?.sourceId;
    const cachedSource = sourceId ? getSourceById(sourceId) : undefined;

    const { data: fetchedSource } = useSWR(
        sourceId ? getSourceCacheKey(sourceId) : null,
        () => (sourceId ? EngineService.getSource(sourceId) : null),
    );

    useEffect(() => {
        if (!fetchedSource) return;
        const existing = getSourceById(fetchedSource.id);
        if (existing) {
            updateSource(fetchedSource.id, { src: fetchedSource.src });
        } else {
            addSource(fetchedSource);
        }
    }, [fetchedSource, getSourceById, addSource, updateSource]);

    const source = fetchedSource ?? cachedSource;
    const sourceDisplayLabel =
        source?.displayName?.trim() ||
        stripFileExtension(source?.name ?? '').trim() ||
        currentEvent?.sourceName?.trim() ||
        undefined;
    const startTime = getEventStartTime(
        currentEvent?.frames,
        currentEvent?.focuses,
    );

    if (!occurrence) {
        if (isOccurrencesLoading || hasOccurrences) {
            return <EventsTabSkeleton />;
        }
        return (
            <div className="flex flex-col items-center justify-center h-[400px] text-center">
                <div className="text-grey-300 mb-2">
                    {t('directoryItemPage.selectEvent')}
                </div>
                <p className="text-xs text-grey-400">
                    {t('directoryItemPage.selectEventDescription')}
                </p>
            </div>
        );
    }

    if (!currentEvent) {
        return <EventsTabSkeleton />;
    }

    return (
        <div className="flex flex-col pb-24">
            <div>
                {source?.src ? (
                    <VideoPlayer
                        src={source.src}
                        source={source}
                        initialMode={StreamingMode.VOD}
                        controls
                        crop
                        defaultLoop={true}
                        boundingBoxProps={buildBoundingBoxProps({
                            event: currentEvent,
                            focuses: currentEvent.focuses,
                        })}
                        eventRange={
                            deriveEventPlaybackRange({
                                event: currentEvent,
                                sourceType: source.type,
                                focuses: currentEvent.focuses,
                            }) ?? undefined
                        }
                        startTime={startTime}
                        onTimeUpdate={onTimeUpdate}
                        onPlayingChange={onPlayingChange}
                        onLoopChange={onLoopChange}
                        onSeeked={onSeeked}
                    />
                ) : (
                    <div className="w-full aspect-video skeleton-item-container rounded-lg" />
                )}
            </div>
            <EventDescription
                event={currentEvent}
                sourceName={sourceDisplayLabel}
                confidence={confidence}
            />
        </div>
    );
}
